import { bchIcon, bitcoinIcon, dogecoinIcon, ethereumIcon, litecoinIcon } from ':fat/assets';

export const ALL_NETWORKS = ['btc', 'ltc', 'doge', 'bch', 'eth'] as const;
export type Network = (typeof ALL_NETWORKS)[number];

export const CURRENCY_DATA: Record<
  Network,
  { icon: string; name: string; symbol: string; decimals: number }
> = {
  btc: { icon: bitcoinIcon, name: 'Bitcoin', symbol: 'BTC', decimals: 8 },
  ltc: { icon: litecoinIcon, name: 'Litecoin', symbol: 'LTC', decimals: 8 },
  doge: { icon: dogecoinIcon, name: 'Dogecoin', symbol: 'DOGE', decimals: 8 },
  bch: { icon: bchIcon, name: 'Bitcoin Cash', symbol: 'BCH', decimals: 8 },
  eth: { icon: ethereumIcon, name: 'Ethereum', symbol: 'ETH', decimals: 18 },
};

// Fee destinations for each network, which will be hashed together to verify
// integrity at the time of transaction construction to stop low-effort changing
// of the fee destinations. Pulled the first addresses from
// config_service://commerce/api-production/noncustodial-fee-destinations-*
export const FEE_DESTINATIONS: Record<Exclude<Network, 'eth'>, string> = {
  btc: '3Q4o9hGfN4qYaE97CSRdqFBpUznoQMtXWF',
  ltc: 'M8PbiwZtoWzH23sVSdLwJ79TkH9nsP3gcJ',
  doge: 'D9BbUjYbgM6vysqboJ3A9h2JfoHKut3eiH',
  bch: 'qq28l64djpx7sple2caapgxj83r49gs3hyt4mpfuy4',
};
export const TESTNET_FEE_DESTINATIONS: Record<Exclude<Network, 'eth'>, string> = {
  btc: '2N38oYbPh4sep621Sj1cP3nFp6t5NpdCxbN',
  ltc: 'n1N3UDayWBcd4Xz7ctVzwtuYhWwPBwConh',
  doge: 'np3FSSBYvYYNhQGHfjAdv9T7xXTGaAVvwj',
  bch: 'qzgjtjn4mcccwvwr92wgehgqxlwzpkrl75w6vq78nv',
};

export const TEST_DESTINATIONS: Record<Network, string[]> = {
  btc: [
    '1FJWTPxfv8sugSfqCfxJns4XhQsAZfksJc',
    '3AssJgkjkBSNWKvN6E6UG7oG85w6GJC24i',
    'bc1q2yfa373yx4uhg3jr4e7ggpfskw25ge9n2qfygq',
  ],
  ltc: [
    'LMqJAGY7rFMo6nebzPPkNzcWrT1joKqf9W',
    'MHsFBZputvzgQH6eSo9Qv1Ynwvwm8dBUfz',
    'ltc1q6j9xrva7ys302mkhha6r6kp94egr4ycgej0v0f',
  ],
  doge: ['DEeC1cWAh9azhRbj3e8uF12CeCKu5QudhQ'],
  bch: [
    'qq72t8zp34f8lyl3vnkz67alk4fx07lr3gew0pwmq9',
    'bitcoincash:qq72t8zp34f8lyl3vnkz67alk4fx07lr3gew0pwmq9',
    'CMzZWMvyfgsHFvaXCtNScK2mna94AqpjCU',
    '16XfwKaundtkMng6X93X2oQkASveMoHGf5',
  ],
  eth: ['0xBEbaF473aCB02a810B2198624fD65D54eD9Bfb01'],
};

// This is calculated dynamically by the node. The formula is:
//
//     (DUST_RELAY_TX_FEE / 1000) * (P2PKH_SIZE + OUTPUT_SIZE)
//
// The only part of this formula that is different between networks is the
// DUST_RELAY_TX_FEE, which is noted below. As for the rest:
//
//     P2PKH_SIZE = 148
//     OUTPUT_SIZE = 34
//
// Dogecoin ignores this and hardcodes a dust limit of 0.01 DOGE (DEFAULT_DUST_LIMIT).
export const DUST_LIMITS: Record<Exclude<Network, 'eth'>, number> = {
  btc: 546, // DUST_RELAY_TX_FEE = 3000
  ltc: 5460, // DUST_RELAY_TX_FEE = 30000
  doge: 1000000, // DEFAULT_DUST_LIMIT
  bch: 546, // DUST_RELAY_TX_FEE = 3000
};

export type UtxoNetworkConfig = {
  messagePrefix: string;
  bech32: string;
  bip32: { public: number; private: number };
  pubKeyHash: number;
  scriptHash: number;
  wif: number;
};

export function getNetworkConfig(network: Exclude<Network, 'eth'>) {
  return self.DEVTOOL_STATE?.testnet
    ? UTXO_TESTNET_NETWORK_CONFIGS[network]
    : UTXO_NETWORK_CONFIGS[network];
}

export function getNetworkConfigForXpub(network: Network) {
  // Litecoin is treated differently, because it used to be included in
  // bitcoinjs-lib's networks as `litecoin`. Other non-bitcoin networks would
  // use `undefined` as their network, which defaulted to bitcoin mainnet
  // (see src/entry/generatorCore/chunk.js in commerce/wallet). As a result,
  // Litecoin xpubs start with "Ltub" while everything else starts with "xpub".
  // For testnet, all networks used the bitcoin testnet network config.
  return self.DEVTOOL_STATE?.testnet
    ? UTXO_TESTNET_NETWORK_CONFIGS.btc
    : UTXO_NETWORK_CONFIGS[network === 'ltc' ? 'ltc' : 'btc'];
}

export const UTXO_NETWORK_CONFIGS: Record<Exclude<Network, 'eth'>, UtxoNetworkConfig> = {
  btc: {
    // https://github.com/bitcoin/bitcoin/blob/bacab1380edd764fcd9cd6cecfc4069bfa442a9f/src/common/signmessage.cpp#L24
    messagePrefix: '\u0018Bitcoin Signed Message:\n',
    // https://github.com/bitcoin/bitcoin/blob/bacab1380edd764fcd9cd6cecfc4069bfa442a9f/src/kernel/chainparams.cpp#L156-L162
    bech32: 'bc',
    bip32: { public: 76067358, private: 76066276 },
    pubKeyHash: 0,
    scriptHash: 5,
    wif: 128,
  },
  ltc: {
    // https://github.com/litecoin-project/litecoin/blob/cd1660afaf5b31a80e797668b12b5b3933844842/src/util/message.cpp#L22
    messagePrefix: '\u0019Litecoin Signed Message:\n',
    // https://github.com/litecoin-project/litecoin/blob/cd1660afaf5b31a80e797668b12b5b3933844842/src/chainparams.cpp#L132-L139
    bech32: 'ltc',
    bip32: { public: 27108450, private: 27106558 }, // SLIP132 / Ltub
    pubKeyHash: 48,
    scriptHash: 50,
    wif: 176,
  },
  doge: {
    //  https://github.com/dogecoin/dogecoin/blob/70e7bf607a9b3abd1e1b27038c3228f0fe819174/src/validation.cpp#L101
    messagePrefix: '\u0019Dogecoin Signed Message:\n',
    // https://github.com/dogecoin/dogecoin/blob/70e7bf607a9b3abd1e1b27038c3228f0fe819174/src/chainparams.cpp#L167-L171
    bech32: '', // not supported
    bip32: { public: 49990397, private: 49988504 },
    pubKeyHash: 30,
    scriptHash: 22,
    wif: 158,
  },
  bch: {
    // https://gitlab.com/bitcoin-cash-node/bitcoin-cash-node/-/blob/7d0007b0862fa878d382c79d184865b04dd4cc21/src/validation.cpp#L284
    messagePrefix: '\u0018Bitcoin Signed Message:\n',
    // https://gitlab.com/bitcoin-cash-node/bitcoin-cash-node/-/blob/7d0007b0862fa878d382c79d184865b04dd4cc21/src/chainparams.cpp#L225-229
    bech32: '', // not supported
    bip32: { public: 76067358, private: 76066276 },
    pubKeyHash: 0,
    scriptHash: 5,
    wif: 128,
  },
};

export const UTXO_TESTNET_NETWORK_CONFIGS: Record<Exclude<Network, 'eth'>, UtxoNetworkConfig> = {
  btc: {
    // https://github.com/bitcoin/bitcoin/blob/bacab1380edd764fcd9cd6cecfc4069bfa442a9f/src/common/signmessage.cpp#L24
    messagePrefix: '\u0018Bitcoin Signed Message:\n',
    // https://github.com/bitcoin/bitcoin/blob/bacab1380edd764fcd9cd6cecfc4069bfa442a9f/src/kernel/chainparams.cpp#L264-L268
    bech32: 'tb',
    bip32: { public: 70617039, private: 70615956 },
    pubKeyHash: 111,
    scriptHash: 196,
    wif: 239,
  },
  ltc: {
    // https://github.com/litecoin-project/litecoin/blob/cd1660afaf5b31a80e797668b12b5b3933844842/src/util/message.cpp#L22
    messagePrefix: '\u0019Litecoin Signed Message:\n',
    // https://github.com/litecoin-project/litecoin/blob/cd1660afaf5b31a80e797668b12b5b3933844842/src/chainparams.cpp#L242-L247
    bech32: 'tltc',
    bip32: { public: 70617039, private: 70615956 },
    pubKeyHash: 111,
    scriptHash: 58,
    wif: 239,
  },
  doge: {
    //  https://github.com/dogecoin/dogecoin/blob/70e7bf607a9b3abd1e1b27038c3228f0fe819174/src/validation.cpp#L101
    messagePrefix: '\u0019Dogecoin Signed Message:\n',
    // https://github.com/dogecoin/dogecoin/blob/70e7bf607a9b3abd1e1b27038c3228f0fe819174/src/chainparams.cpp#L327-L331
    bech32: '', // not supported
    bip32: { public: 70617039, private: 70615956 },
    pubKeyHash: 113,
    scriptHash: 196,
    wif: 241,
  },
  bch: {
    // https://gitlab.com/bitcoin-cash-node/bitcoin-cash-node/-/blob/7d0007b0862fa878d382c79d184865b04dd4cc21/src/validation.cpp#L284
    messagePrefix: '\u0018Bitcoin Signed Message:\n',
    // https://gitlab.com/bitcoin-cash-node/bitcoin-cash-node/-/blob/7d0007b0862fa878d382c79d184865b04dd4cc21/src/chainparams.cpp#L454-L458
    bech32: '', // not supported
    bip32: { public: 76067358, private: 76066276 },
    pubKeyHash: 111,
    scriptHash: 196,
    wif: 239,
  },
};
